import axios from 'axios'
import {SURFACES, buildPayload, groupQueueUrl} from '@/components/filters/capabilities'

/**
 * Bounding-box tagging state. [IT-642]
 *
 * Holds the group being worked, the box selection, and the pending tag diff.
 *
 * Share of shelf is computed here rather than fetched on every change: the
 * tagger needs it to move the instant a tag is applied, and a round-trip per
 * apply would make the sidebar lag behind the canvas. The server owns the
 * authoritative number (PhotoGroup.share_of_shelf) and uses the identical
 * rules — there is a test pinning the two implementations together.
 */

const state = {
  group: null,          // {id, response_group_id, question_id, photos: [...]}
  activePhotoId: null,
  // null = all categories (whole-box untagged); an id scopes the view to it.
  activeCategoryId: null,
  selectedBoxIds: [],
  // Pending tag intent, keyed by tag id: 'add' | 'remove'. Cleared on apply.
  pendingTags: {},
  viewMode: 'focus',    // 'focus' | 'group'
  // Active shared-filter payload scoping the queue (retailer / rgid / project /
  // job / date / department). Persisted here so "Submit & Get Next Group"
  // stays within the current filter. [IT-744]
  groupFilter: {},
  // Box-QC [IT-745]: the quality_control batch (locked to this reviewer) worked
  // one group at a time; `group` tracks the current one, qcIndex its position.
  qcGroups: [],
  qcIndex: 0,
  qcOnlyNeedsReview: false,
  // Box-QC [IT-788]: include groups already reviewed in the queue. Off by
  // default (the queue is unreviewed work); on lets a reviewer pull an
  // already-QCd group back up — e.g. search an RGID to fix an outlier.
  qcShowQced: false,
  isLoading: false,
  isSaving: false,
}

/**
 * "Tagged" as the tagger currently sees it. With no active category the whole
 * box counts (any human tag); with one selected, only a tag IN that category
 * counts — so switching category re-surfaces boxes still missing it. [IT-719]
 */
function taggedInView(box, categoryTagIds) {
  if (categoryTagIds == null) return !!box.tagged
  return (box.tag_ids || []).some(id => categoryTagIds.includes(id))
}

const getters = {
  /** Every box across every photo in the group, each stamped with its photo. */
  allBoxes: (state) => {
    if (!state.group) return []
    return state.group.photos.flatMap(photo =>
      (photo.boxes || []).map(box => ({...box, photo_id: photo.id}))
    )
  },

  activePhoto: (state) => {
    if (!state.group) return null
    return state.group.photos.find(p => p.id === state.activePhotoId) || state.group.photos[0] || null
  },

  /** Boxes on the currently-focused photo. */
  activePhotoBoxes: (state, getters) => {
    const photo = getters.activePhoto
    if (!photo) return []
    return (photo.boxes || []).map(box => ({...box, photo_id: photo.id}))
  },

  selectedBoxes: (state, getters) =>
    getters.allBoxes.filter(box => state.selectedBoxIds.includes(box.id)),

  /** Active (non-invalid) boxes — the set share-of-shelf is measured over. */
  activeBoxes: (state, getters) => getters.allBoxes.filter(box => box.status === 'active'),

  /**
   * Tag ids of the active category, or null when none is selected (= whole-box).
   * Reads the tags module if present; tolerates its absence so the boxes module
   * stays testable in isolation.
   */
  activeCategoryTagIds: (state, getters, rootState) => {
    if (state.activeCategoryId == null) return null
    const tags = (rootState.tags && rootState.tags.tags) || []
    return tags.filter(t => t.category && t.category.id === state.activeCategoryId).map(t => t.id)
  },

  /**
   * Ids of the Rejection reason tags {invalid, duplicate}, resolved from the
   * catalog (a trigger_delete category). Null when the tenant is unseeded. [IT-720]
   */
  rejectionTagIds: (state, getters, rootState) => {
    const tags = (rootState.tags && rootState.tags.tags) || []
    const byName = name => {
      const t = tags.find(x => x.category && x.category.trigger_delete && x.name === name)
      return t ? t.id : null
    }
    return {invalid: byName('Invalid detection'), duplicate: byName('Duplicate')}
  },

  untaggedCount: (state, getters) =>
    getters.activeBoxes.filter(box => !taggedInView(box, getters.activeCategoryTagIds)).length,

  totalActiveCount: (state, getters) => getters.activeBoxes.length,

  /**
   * Boxes that keep the group out of "done", using the server's own rule
   * (PhotoGroup.refresh_tagged_from_boxes): an active box carrying no human tag.
   * Category-AGNOSTIC on purpose — gating submit on the active-category view
   * would let a tagger submit after finishing one category and flip as they
   * switch categories. Rejected (invalid/duplicate) boxes are not active, so
   * they never block.
   */
  submitBlockedCount: (state, getters) => getters.activeBoxes.filter(box => !box.tagged).length,

  /**
   * The group may be submitted iff it has at least one active box and none is
   * untagged — the exact condition the backend uses to roll `tagged` up to the
   * group, so the Submit button and the server agree on "complete".
   */
  groupSubmittable: (state, getters) =>
    getters.totalActiveCount > 0 && getters.submitBlockedCount === 0,

  /** Untagged boxes on the focused photo — drives the filmstrip badge. */
  untaggedOnPhoto: (state, getters) => (photoId) => {
    if (!state.group) return 0
    const photo = state.group.photos.find(p => p.id === photoId)
    if (!photo) return 0
    const categoryTagIds = getters.activeCategoryTagIds
    return (photo.boxes || []).filter(b => b.status === 'active' && !taggedInView(b, categoryTagIds)).length
  },

  /**
   * Per-tag share of shelf across the whole group.
   *
   * ⚠️ Implemented TWICE — here for the live sidebar, and in
   * PhotoGroup.share_of_shelf() for the API. They must agree, or the number a
   * tagger sees will not match the number the client API reports. Change one,
   * change both; the two test suites mirror each other's fixtures on purpose.
   *
   * Mirrors PhotoGroup.share_of_shelf exactly — width-based; untagged active
   * boxes in the denominator; invalid boxes in neither; a multi-tag box
   * contributing its FULL width to each tag (so shares can sum past 1.0
   * across independent categories, which is correct).
   */
  shareOfShelf: (state, getters) => {
    const boxes = getters.activeBoxes
    const totalWidth = boxes.reduce((sum, box) => sum + box.width, 0)
    if (!totalWidth) return {}

    const perTag = {}
    boxes.forEach(box => {
      (box.tag_ids || []).forEach(tagId => {
        perTag[tagId] = (perTag[tagId] || 0) + box.width
      })
    })

    const shares = {}
    Object.keys(perTag).forEach(tagId => { shares[tagId] = perTag[tagId] / totalWidth })
    return shares
  },

  /** Fraction of shelf width carrying no tag at all. */
  untaggedShare: (state, getters) => {
    const boxes = getters.activeBoxes
    const totalWidth = boxes.reduce((sum, box) => sum + box.width, 0)
    if (!totalWidth) return 0
    const untaggedWidth = boxes
      .filter(box => !(box.tag_ids || []).length)
      .reduce((sum, box) => sum + box.width, 0)
    return untaggedWidth / totalWidth
  },

  /**
   * Tri-state for a tag against the current selection:
   *   'all'  — every selected box already has it
   *   'some' — a mix (renders indeterminate)
   *   'none' — no selected box has it
   */
  tagStateForSelection: (state, getters) => (tagId) => {
    const selected = getters.selectedBoxes
    if (!selected.length) return 'none'
    const withTag = selected.filter(box => (box.tag_ids || []).includes(tagId)).length
    if (withTag === 0) return 'none'
    if (withTag === selected.length) return 'all'
    return 'some'
  },

  pendingAddIds: (state) =>
    Object.keys(state.pendingTags).filter(id => state.pendingTags[id] === 'add').map(Number),

  pendingRemoveIds: (state) =>
    Object.keys(state.pendingTags).filter(id => state.pendingTags[id] === 'remove').map(Number),

  hasPendingChanges: (state) => Object.keys(state.pendingTags).length > 0,
}

const mutations = {
  setBoxGroup(state, group) {
    state.group = group
    state.activePhotoId = group && group.photos.length ? group.photos[0].id : null
    state.selectedBoxIds = []
    state.pendingTags = {}
  },
  setActiveCategory(state, categoryId) {
    state.activeCategoryId = categoryId
    // The pending diff was built against the old category's tags; drop it so
    // it can't apply after the switch. Selection is kept — same boxes.
    state.pendingTags = {}
  },
  setActivePhoto(state, photoId) {
    state.activePhotoId = photoId
    // Selection is per-photo in focus mode; carrying it across photos would let
    // an unseen box get tagged by a selection the user can no longer see.
    state.selectedBoxIds = []
    state.pendingTags = {}
  },
  setSelection(state, boxIds) {
    state.selectedBoxIds = boxIds
    state.pendingTags = {}
  },
  toggleBoxSelection(state, boxId) {
    const idx = state.selectedBoxIds.indexOf(boxId)
    if (idx === -1) state.selectedBoxIds.push(boxId)
    else state.selectedBoxIds.splice(idx, 1)
    state.pendingTags = {}
  },
  addToSelection(state, boxIds) {
    const merged = new Set(state.selectedBoxIds)
    boxIds.forEach(id => merged.add(id))
    state.selectedBoxIds = Array.from(merged)
    state.pendingTags = {}
  },
  removeFromSelection(state, boxIds) {
    state.selectedBoxIds = state.selectedBoxIds.filter(id => !boxIds.includes(id))
    state.pendingTags = {}
  },
  clearSelection(state) {
    state.selectedBoxIds = []
    state.pendingTags = {}
  },
  setPendingTag(state, {tagId, intent}) {
    // Vue 2 reactivity: replace the object rather than mutating a key in place.
    const next = {...state.pendingTags}
    if (intent === null) delete next[tagId]
    else next[tagId] = intent
    state.pendingTags = next
  },
  clearPendingTags(state) {
    state.pendingTags = {}
  },
  setViewMode(state, mode) {
    state.viewMode = mode
  },
  setGroupFilter(state, filter) {
    state.groupFilter = filter || {}
  },
  setQcBatch(state, groups) {
    state.qcGroups = groups || []
    state.qcIndex = 0
  },
  setQcIndex(state, index) {
    state.qcIndex = index
  },
  setQcOnlyNeedsReview(state, value) {
    state.qcOnlyNeedsReview = !!value
  },
  setQcShowQced(state, value) {
    state.qcShowQced = !!value
  },
  setBoxesLoading(state, value) {
    state.isLoading = value
  },
  setBoxesSaving(state, value) {
    state.isSaving = value
  },
  /** Patch boxes in place after a save so the canvas updates without a refetch. */
  applyBoxUpdates(state, updates) {
    if (!state.group) return
    const byId = {}
    updates.forEach(u => { byId[u.id] = u })
    state.group.photos.forEach(photo => {
      photo.boxes = (photo.boxes || []).map(box => byId[box.id] ? {...box, ...byId[box.id]} : box)
    })
    // Reassign so Vue 2 picks up the nested change.
    state.group = {...state.group}
  },
  /** Add a newly-created (human-drawn) box to its photo. [IT-722] */
  addBox(state, box) {
    if (!state.group) return
    const photo = state.group.photos.find(p => p.id === box.photo)
    if (!photo) return
    photo.boxes = [...(photo.boxes || []), box]
    state.group = {...state.group}
  },
  /** Drop a box locally after a soft-delete. [IT-722] */
  removeBox(state, boxId) {
    if (!state.group) return
    state.group.photos.forEach(photo => {
      photo.boxes = (photo.boxes || []).filter(b => b.id !== boxId)
    })
    state.selectedBoxIds = state.selectedBoxIds.filter(id => id !== boxId)
    state.group = {...state.group}
  },
}

const actions = {
  /**
   * Check out the next group for this tagger.
   *
   * Reuses the existing group queue endpoint rather than adding a bbox-specific
   * one — it already owns assignment, stale-assignment release, and
   * oldest-photo-first ordering, none of which differ for boxes. It returns a
   * list (the queue), and we work one group at a time.
   */
  fetchBoxGroup(context) {
    context.commit('setBoxesLoading', true)
    // Scope the queue by the active filter, via the shared builder used by
    // the group-mode queue. [IT-744]
    return axios.get(groupQueueUrl('/api/photo-groups/get_groups/', context.state.groupFilter))
      .then(response => {
        const groups = Array.isArray(response.data) ? response.data : []
        context.commit('setBoxGroup', groups.length ? groups[0] : null)
        return groups
      })
      .finally(() => context.commit('setBoxesLoading', false))
  },

  /**
   * Finish the assigned group: mark it done + release the tagger, mirroring the
   * other tagging modes' "Submit" (POST /api/photo-groups/submit_assigned/,
   * which fires the finish verification event and clears the assignment). Box
   * mode is handed exactly one group by get_groups, so submitting the assigned
   * set submits this group.
   *
   * With getNext the next group is checked out; otherwise the view drops to its
   * empty state. On failure nothing is cleared locally so the tagger can retry.
   */
  submitBoxGroup(context, {getNext = true} = {}) {
    context.commit('setBoxesSaving', true)
    return axios.post('/api/photo-groups/submit_assigned/')
      .then(() => {
        if (getNext) return context.dispatch('fetchBoxGroup')
        context.commit('setBoxGroup', null)
        return null
      })
      .finally(() => context.commit('setBoxesSaving', false))
  },

  // --- box-QC (IT-745) -------------------------------------------------

  /**
   * Check out a QC batch (quality_control locks up to 10 tagged/unreviewed
   * groups to this reviewer) and work the first one. Reuses the active
   * shared filter plus the QC-only "only needs review" and "show already QCed"
   * toggles. Same POST-body key mapping group-QC uses — including `showQCed`,
   * which the server reads to drop the default `reviewed=False` filter. [IT-788]
   *
   * Both toggles are persisted to state (not just read from the arg) so the
   * drain-refetch in finalizeQcGroup, which dispatches with no args, keeps the
   * reviewer's current queue scope.
   */
  fetchQcBatch(context, {onlyNeedsReview, showQCed} = {}) {
    if (onlyNeedsReview !== undefined) context.commit('setQcOnlyNeedsReview', onlyNeedsReview)
    if (showQCed !== undefined) context.commit('setQcShowQced', showQCed)
    context.commit('setBoxesLoading', true)
    const f = context.state.groupFilter || {}
    // IT-707 P2.3: this used to hand-build the body, which made it a fifth
    // parallel payload builder for a surface the shared one already covers. It
    // diverged twice before anyone noticed and a third time the moment IT-708
    // added includeInactive — the audit caught that one, which is why this is
    // now the shared builder rather than another hand-edit.
    //
    // The two QC toggles are merged in afterwards on purpose: box-QC owns them
    // in its own state (so the panel's checkboxes survive a filter change),
    // not in the shared filter payload.
    const body = {
      ...buildPayload(SURFACES.GROUP_QC, f),
      onlyNeedsReview: context.state.qcOnlyNeedsReview,
      showQCed: context.state.qcShowQced,
    }
    return axios.post('/api/photo-groups/quality_control/', body)
      .then(response => {
        const groups = Array.isArray(response.data) ? response.data : []
        context.commit('setQcBatch', groups)
        context.commit('setBoxGroup', groups.length ? groups[0] : null)
        return groups
      })
      .finally(() => context.commit('setBoxesLoading', false))
  },

  approveQcGroup(context) {
    return context.dispatch('finalizeQcGroup', {action: 'review_group'})
  },

  flagQcGroup(context) {
    return context.dispatch('finalizeQcGroup', {action: 'flag_group'})
  },

  /**
   * Finalize the current QC group server-side (review_group / flag_group, which
   * release the lock), then advance to the next group in the held batch — or
   * pull a fresh batch once this one is drained.
   */
  finalizeQcGroup(context, {action}) {
    const current = context.state.group
    if (!current) return Promise.resolve(null)
    context.commit('setBoxesSaving', true)
    return axios.post(`/api/photo-groups/${action}/`, {group_id: current.id})
      .then(() => {
        const next = context.state.qcIndex + 1
        if (next < context.state.qcGroups.length) {
          context.commit('setQcIndex', next)
          context.commit('setBoxGroup', context.state.qcGroups[next])
          return null
        }
        return context.dispatch('fetchQcBatch')
      })
      .finally(() => context.commit('setBoxesSaving', false))
  },

  /**
   * Push the pending tag diff (or an invalid-flag) onto the selection.
   *
   * Sends box ids rather than a filter so the server acts on exactly what the
   * user saw selected — no chance of a concurrent change widening the blast
   * radius between render and submit.
   */
  applyBoxTags(context, {markInvalid = false, markDuplicate = false, invalidReason = '', isQc = false} = {}) {
    const boxIds = context.state.selectedBoxIds
    if (!boxIds.length) return Promise.resolve(null)

    // State which photo each box belongs to, so the server never infers it.
    // In group view a marquee legitimately crosses photo boundaries; sending
    // only the focused photo made the server drop the rest, match nothing and
    // 404 — which surfaced as "Could not save". Sending the pairing (rather
    // than two flat lists that merely intersect) also means a box paired with
    // the wrong photo is rejected outright instead of silently skipped.
    const byPhoto = new Map()
    context.getters.selectedBoxes.forEach(box => {
      if (!byPhoto.has(box.photo_id)) byPhoto.set(box.photo_id, [])
      byPhoto.get(box.photo_id).push(box.id)
    })

    const payload = {
      photos: Array.from(byPhoto, ([photoId, ids]) => ({photo_id: photoId, box_ids: ids})),
      add_tag_ids: context.getters.pendingAddIds,
      remove_tag_ids: context.getters.pendingRemoveIds,
      mark_invalid: markInvalid,
      mark_duplicate: markDuplicate,
      invalid_reason: invalidReason,
      isQc,
    }

    context.commit('setBoxesSaving', true)
    return axios.post('/api/bounding-boxes/bulk_update/', payload)
      .then(response => {
        // IT-749: the server returns the authoritative box rows (tagged/status/
        // tag_ids) — the completion rule lives server-side only, so when they are
        // present we apply them verbatim instead of recomputing locally.
        const serverBoxes = response.data && response.data.boxes
        if (serverBoxes && serverBoxes.length) {
          context.commit('applyBoxUpdates', serverBoxes.map(b => ({
            id: b.id,
            tag_ids: b.tag_ids,
            tagged: b.tagged,
            status: b.status,
          })))
          context.commit('clearPendingTags')
          context.commit('clearSelection')
          return response.data
        }
        // Fallback (older server without `boxes`): reflect the change locally
        // rather than refetching the group — the user is mid-flow and a refetch
        // would blank the canvas for a beat.
        const addIds = payload.add_tag_ids
        const removeIds = payload.remove_tag_ids
        const reject = context.getters.rejectionTagIds
        const updates = context.getters.selectedBoxes.map(box => {
          if (markInvalid || markDuplicate) {
            // Reflect the reason tag so the canvas can split invalid vs duplicate
            // immediately; status drives the red/invalid styling either way.
            const reasonId = markDuplicate ? reject.duplicate : reject.invalid
            return {id: box.id, status: 'invalid', tag_ids: reasonId ? [reasonId] : [], tagged: false}
          }
          const next = new Set(box.tag_ids || [])
          removeIds.forEach(id => next.delete(id))
          // Reactivating (a classification tag added to a rejected box) drops the
          // rejection tag server-side; mirror that here so the live share-of-shelf
          // sidebar doesn't briefly show a phantom Invalid/Duplicate share. [IT-720]
          if (box.status === 'invalid' && addIds.length) {
            if (reject.invalid != null) next.delete(reject.invalid)
            if (reject.duplicate != null) next.delete(reject.duplicate)
          }
          addIds.forEach(id => next.add(id))
          const tagIds = Array.from(next)
          return {
            id: box.id,
            tag_ids: tagIds,
            tagged: tagIds.length > 0,
            status: box.status === 'invalid' && addIds.length ? 'active' : box.status,
          }
        })
        context.commit('applyBoxUpdates', updates)
        context.commit('clearPendingTags')
        context.commit('clearSelection')
        return response.data
      })
      .finally(() => context.commit('setBoxesSaving', false))
  },

  /** Create a human-drawn box, then select it for immediate tagging. [IT-722] */
  createBox(context, {photoId, x, y, width, height}) {
    return axios.post('/api/bounding-boxes/create_box/', {photo_id: photoId, x, y, width, height})
      .then(response => {
        context.commit('addBox', response.data)
        context.commit('setSelection', [response.data.id])
        return response.data
      })
  },

  /** Move or resize an existing box. [IT-722] */
  updateBoxGeometry(context, {boxId, x, y, width, height}) {
    // Commit locally first so the box holds its new size on release instead of
    // snapping back to the old one until the round-trip lands. [IT-722]
    context.commit('applyBoxUpdates', [{id: boxId, x, y, width, height}])
    return axios.post('/api/bounding-boxes/update_geometry/', {box_id: boxId, x, y, width, height})
  },

  /** Soft-delete a box and drop it from the canvas. [IT-722] */
  deleteBox(context, boxId) {
    return axios.post('/api/bounding-boxes/delete_box/', {box_id: boxId})
      .then(() => context.commit('removeBox', boxId))
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}
